// @ts-check
import { gql } from '@apollo/client';
import { Document } from '@react-pdf/renderer';
import React from 'react';

/**
 * @template {{ [x: string]: any; }} Props
 * @param {React.ComponentType<Props>
 * & {
 *  fragment: import('graphql').DocumentNode;
 *  beforeRender?:  import('./Preview').BeforeRenderEvent;
 *  afterRender?:  import('./Preview').AfterRenderEvent;
 * }} Catalog
 * @param {any} [options]
 */
export default function asCatalog(Catalog, options) {
  return {
    beforeQuery,
    beforeRender: Catalog.beforeRender,
    query: getQuery(Catalog.fragment),
    layout: ({ data, favorites, ...rest }) => {
      const { collection, catalog } = afterQuery({
        data,
        favorites,
      });
      return (
        <Document
          onRender={Catalog.afterRender}
          title={`${collection?.name} Catalog`}
        >
          <Catalog data={catalog} {...options} {...rest} />
        </Document>
      );
    },
  };
}

/**
 * Creates the query variables.
 * @param {object} props
 * @param {string} props.collectionId The id of the collection.
 * @param {import('@bottlebooks/graphql-server/src').ContentLocale} [props.locale] The locale - en by default.
 * @returns {import('@apollo/client').QueryHookOptions<any, {
 *  collectionId: string;
 *  locale: import('@bottlebooks/graphql-server/src').ContentLocale;
 * }>} */
function beforeQuery({ collectionId, locale }) {
  return {
    variables: {
      collectionId,
      locale,
    },
  };
}

/**
 *
 * Creates a query from a fragment.
 * @param {import('graphql').DocumentNode} fragment
 */
function getQuery(fragment) {
  const firstDefinition = fragment.definitions[0];
  if (firstDefinition?.kind !== 'FragmentDefinition')
    throw new Error('You need to pass a fragment!');
  const onType = firstDefinition.typeCondition.name.value;
  const parse = gql; // Renamed to avoid IDE errors
  if (onType !== 'CatalogConfiguration') {
    throw new Error('You must pass a fragment on CatalogConfiguration');
  }
  return parse/* GraphQL */ `
    query Catalog_${firstDefinition.name.value}(
      $collectionId: ID!
      $locale: ContentLocale!
    ) {
      collection(
        collectionId: $collectionId
        locale: $locale
      ) {
        ... on Event {
          eventId # Needed for apollo-client.
        }
        collectionId
        name
        catalog {
          catalogConfigurationId
          ...${firstDefinition.name.value}
        }
      }
    }

    ${fragment}
  `;
}

function afterQuery({ data, favorites }) {
  const catalog = data?.collection?.catalog;
  const sections = favorites
    ? filterOnFavorites({ sections: catalog.sections, favorites })
    : catalog.sections;
  return {
    collection: data?.collection,
    catalog: { ...catalog, sections },
  };
}

function filterOnFavorites({ favorites, sections }) {
  const exhibitorIds = favorites.map(({ exhibitorId }) => exhibitorId);
  const fiteredSections = sections?.filter(
    (section) =>
      section.__typename === 'RegistrationCatalogSection' &&
      exhibitorIds.includes(section.registration.companyId)
  );
  const enrichedSections = fiteredSections.map((section) => {
    const enrichedProducts = section.registration.registeredProducts.nodes.map(
      (product) => {
        const favorite = favorites.find(
          (favoritedProduct) =>
            favoritedProduct.productId === product.product.productId
        );
        return {
          ...product,
          // This information is used to render personal information in the custom catalog
          isFavorite: !!favorite,
          note: favorite?.note,
          rating: favorite?.rating,
        };
      }
    );
    return {
      ...section,
      registration: {
        ...section.registration,
        registeredProducts: { nodes: enrichedProducts },
      },
    };
  });
  return enrichedSections;
}
